// M05 百分法 - 主數據文件
// Percentage Method - Main Data File
// 系統化設計，準備後續 Supabase 數據遷移

// 導入分割數據文件
import {
  M05_01_PROBLEMS,
  M05_02_PROBLEMS,
  M05_03_PROBLEMS,
  M05_04_PROBLEMS,
  M05_05_PROBLEMS
} from './M05_Topics_01_to_05.js';

import {
  M05_06_PROBLEMS,
  M05_07_PROBLEMS,
  M05_08_PROBLEMS,
  M05_09_PROBLEMS,
  M05_10_PROBLEMS
} from './M05_Topics_06_to_10.js';

import {
  M05_11_PROBLEMS,
  M05_12_PROBLEMS,
  M05_13_PROBLEMS,
  M05_14_PROBLEMS,
  M05_15_PROBLEMS
} from './M05_Topics_11_to_15.js';

// M05 模組基本信息
export const M05_MODULE_INFO = {
  id: "M05",
  title: "百分法",
  titleEn: "Percentage Method",
  description: "全面學習百分數的概念、計算方法和實際應用，包括分數轉換、利潤計算、利率計算、濃度問題等",
  difficulty: "中級",
  totalTopics: 15,
  totalProblems: 90,
  estimatedHours: 10,
  prerequisites: ["M01 數系與近似值", "M02 恆等式", "基本分數運算"],
  applications: ["商業計算", "金融理財", "統計分析", "化學濃度", "日常生活"],
  learningObjectives: [
    "掌握百分數的基本概念和轉換方法",
    "熟練進行各種百分數計算",
    "理解單利和複利的區別與應用",
    "掌握商業中的利潤、折扣計算",
    "能夠解決濃度、混合等實際問題",
    "應用百分數分析統計數據和變化趨勢"
  ]
};

// M05 主題列表
export const M05_TOPICS = [
  {
    id: "M05_01",
    title: "分數轉百分數",
    titleEn: "Fraction to Percentage Conversion",
    description: "學習將分數、小數轉換為百分數的各種方法",
    difficulty: 1,
    estimatedMinutes: 20,
    problemCount: 6,
    keySkills: ["分數轉換", "小數轉換", "循環小數處理"],
    prerequisites: ["分數基本運算", "小數運算"]
  },
  {
    id: "M05_02",
    title: "百分數計算",
    titleEn: "Percentage Calculations",
    description: "掌握基本的百分數乘法、除法和混合運算",
    difficulty: 1,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["百分數乘法", "連續計算", "方程建立"],
    prerequisites: ["百分數概念", "基本代數"]
  },
  {
    id: "M05_03",
    title: "原數還原",
    titleEn: "Original Number Recovery",
    description: "學習從百分數結果反推原始數值的方法",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["逆向思維", "方程求解", "多步還原"],
    prerequisites: ["百分數計算", "方程求解"]
  },
  {
    id: "M05_04",
    title: "百分增加",
    titleEn: "Percentage Increase",
    description: "掌握百分增加的計算和增長率分析",
    difficulty: 2,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["增長率計算", "複合增長", "指數模型"],
    prerequisites: ["百分數計算", "指數概念"]
  },
  {
    id: "M05_05",
    title: "變化百分數",
    titleEn: "Percentage Change",
    description: "學習計算和分析各種變化的百分數表示",
    difficulty: 2,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["變化率計算", "比較分析", "連續變化"],
    prerequisites: ["百分增加", "正負數概念"]
  },
  {
    id: "M05_06",
    title: "連續百分變化",
    titleEn: "Continuous Percentage Changes",
    description: "掌握多次連續百分變化的計算和分析",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["連續變化", "複合效應", "指數增長"],
    prerequisites: ["變化百分數", "指數運算"]
  },
  {
    id: "M05_07",
    title: "利潤計算",
    titleEn: "Profit Calculations",
    description: "學習商業中的成本、售價、利潤率計算",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["利潤率", "成本分析", "定價策略"],
    prerequisites: ["百分數計算", "商業常識"]
  },
  {
    id: "M05_08",
    title: "多重折扣",
    titleEn: "Multiple Discounts",
    description: "掌握各種折扣方式的計算和比較",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["連續折扣", "階梯折扣", "組合優惠"],
    prerequisites: ["百分數計算", "連續變化"]
  },
  {
    id: "M05_09",
    title: "標價策略分析",
    titleEn: "Pricing Strategy Analysis",
    description: "分析各種商業定價策略的數學原理",
    difficulty: 3,
    estimatedMinutes: 40,
    problemCount: 6,
    keySkills: ["定價策略", "市場分析", "價值定價"],
    prerequisites: ["利潤計算", "經濟概念"]
  },
  {
    id: "M05_10",
    title: "單利公式",
    titleEn: "Simple Interest Formula",
    description: "學習單利的計算公式和實際應用",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["單利公式", "利息計算", "時間換算"],
    prerequisites: ["百分數計算", "時間概念"]
  },
  {
    id: "M05_11",
    title: "複利公式",
    titleEn: "Compound Interest Formula",
    description: "掌握複利計算和與單利的比較分析",
    difficulty: 3,
    estimatedMinutes: 40,
    problemCount: 6,
    keySkills: ["複利公式", "指數增長", "投資分析"],
    prerequisites: ["單利公式", "指數運算"]
  },
  {
    id: "M05_12",
    title: "有效利率計算",
    titleEn: "Effective Interest Rate Calculation",
    description: "學習名義利率與有效利率的轉換和應用",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["有效利率", "複利頻率", "實際收益"],
    prerequisites: ["複利公式", "金融概念"]
  },
  {
    id: "M05_13",
    title: "基本百分數應用",
    titleEn: "Basic Percentage Applications",
    description: "掌握日常生活中百分數的各種應用",
    difficulty: 1,
    estimatedMinutes: 25,
    problemCount: 6,
    keySkills: ["統計分析", "比例計算", "實際應用"],
    prerequisites: ["百分數基本概念"]
  },
  {
    id: "M05_14",
    title: "反向百分數",
    titleEn: "Reverse Percentage",
    description: "學習從結果反推原始條件的百分數問題",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 6,
    keySkills: ["逆向思維", "方程建立", "條件分析"],
    prerequisites: ["原數還原", "方程求解"]
  },
  {
    id: "M05_15",
    title: "混合濃度計算",
    titleEn: "Mixed Concentration Calculations",
    description: "掌握化學和物理中的濃度、混合問題",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 6,
    keySkills: ["濃度計算", "混合問題", "方程組"],
    prerequisites: ["百分數計算", "方程組"]
  }
];

// 合併所有問題數據
export const M05_PROBLEMS_DATA = {
  "M05_01": M05_01_PROBLEMS,
  "M05_02": M05_02_PROBLEMS,
  "M05_03": M05_03_PROBLEMS,
  "M05_04": M05_04_PROBLEMS,
  "M05_05": M05_05_PROBLEMS,
  "M05_06": M05_06_PROBLEMS,
  "M05_07": M05_07_PROBLEMS,
  "M05_08": M05_08_PROBLEMS,
  "M05_09": M05_09_PROBLEMS,
  "M05_10": M05_10_PROBLEMS,
  "M05_11": M05_11_PROBLEMS,
  "M05_12": M05_12_PROBLEMS,
  "M05_13": M05_13_PROBLEMS,
  "M05_14": M05_14_PROBLEMS,
  "M05_15": M05_15_PROBLEMS
};

// 輔助函數：根據主題ID獲取問題
export const getProblemsByTopicId = (topicId) => {
  return M05_PROBLEMS_DATA[topicId] || [];
};

// 輔助函數：獲取所有M05問題
export const getAllM05Problems = () => {
  return Object.values(M05_PROBLEMS_DATA).flat();
};

// 輔助函數：根據難度獲取問題
export const getProblemsByDifficulty = (difficulty) => {
  return getAllM05Problems().filter(problem => problem.difficulty === difficulty);
};

// 輔助函數：獲取主題信息
export const getTopicById = (topicId) => {
  return M05_TOPICS.find(topic => topic.id === topicId);
};

// 輔助函數：搜索問題
export const searchProblems = (keyword) => {
  const allProblems = getAllM05Problems();
  return allProblems.filter(problem =>
    problem.question.includes(keyword) ||
    problem.explanation.includes(keyword) ||
    problem.hint.includes(keyword)
  );
};

// 輔助函數：按應用領域分類問題
export const getProblemsByApplication = (application) => {
  const applicationKeywords = {
    "商業": ["商品", "售價", "成本", "利潤", "折扣", "定價"],
    "金融": ["利率", "投資", "借款", "複利", "單利", "收益"],
    "統計": ["調查", "比例", "分佈", "分析", "統計"],
    "化學": ["濃度", "溶液", "混合", "稀釋"],
    "日常": ["生活", "學生", "班級", "家庭", "消費"]
  };
  
  const keywords = applicationKeywords[application] || [];
  return getAllM05Problems().filter(problem =>
    keywords.some(keyword => problem.question.includes(keyword))
  );
};

// 輔助函數：獲取學習路徑推薦
export const getRecommendedLearningPath = (currentLevel = "beginner") => {
  const paths = {
    beginner: [
      "M05_01", "M05_02", "M05_13", "M05_03", "M05_04", "M05_05",
      "M05_07", "M05_10", "M05_14", "M05_08", "M05_06", "M05_15",
      "M05_11", "M05_12", "M05_09"
    ],
    intermediate: [
      "M05_01", "M05_02", "M05_03", "M05_04", "M05_05", "M05_06",
      "M05_07", "M05_08", "M05_10", "M05_11", "M05_13", "M05_14",
      "M05_15", "M05_12", "M05_09"
    ],
    advanced: [
      "M05_09", "M05_12", "M05_06", "M05_08", "M05_11", "M05_15",
      "M05_14", "M05_07", "M05_05", "M05_04", "M05_03", "M05_02",
      "M05_10", "M05_13", "M05_01"
    ]
  };
  
  return paths[currentLevel] || paths.beginner;
};

// M05 統計信息
export const M05_STATISTICS = {
  totalProblems: getAllM05Problems().length,
  totalTopics: M05_TOPICS.length,
  difficultyDistribution: {
    level1: getProblemsByDifficulty(1).length,
    level2: getProblemsByDifficulty(2).length,
    level3: getProblemsByDifficulty(3).length,
    level4: getProblemsByDifficulty(4).length
  },
  averageDifficulty: (
    getAllM05Problems().reduce((sum, problem) => sum + problem.difficulty, 0) /
    getAllM05Problems().length
  ).toFixed(1),
  estimatedStudyTime: M05_TOPICS.reduce((sum, topic) => sum + topic.estimatedMinutes, 0),
  topicsByDifficulty: {
    beginner: M05_TOPICS.filter(topic => topic.difficulty === 1).length,
    intermediate: M05_TOPICS.filter(topic => topic.difficulty === 2).length,
    advanced: M05_TOPICS.filter(topic => topic.difficulty === 3).length,
    expert: M05_TOPICS.filter(topic => topic.difficulty === 4).length
  },
  applicationAreas: {
    commercial: getProblemsByApplication("商業").length,
    financial: getProblemsByApplication("金融").length,
    statistical: getProblemsByApplication("統計").length,
    chemical: getProblemsByApplication("化學").length,
    daily: getProblemsByApplication("日常").length
  },
  keyFeatures: [
    "完整覆蓋15個核心主題",
    "系統化90題練習",
    "商業金融應用豐富",
    "理論實踐並重",
    "難度層次分明",
    "生活應用廣泛"
  ],
  learningOutcomes: [
    "掌握百分數基本概念和計算方法",
    "能夠進行複雜的百分數運算和分析",
    "理解並應用單利和複利計算",
    "掌握商業中的定價、折扣、利潤計算",
    "能夠解決濃度、混合等科學問題",
    "具備用百分數分析統計數據的能力"
  ]
};

// M05 主題依賴關係
export const M05_DEPENDENCIES = {
  prerequisites: {
    "M05_01": [],
    "M05_02": ["M05_01"],
    "M05_03": ["M05_02"],
    "M05_04": ["M05_02"],
    "M05_05": ["M05_04"],
    "M05_06": ["M05_05"],
    "M05_07": ["M05_02"],
    "M05_08": ["M05_06", "M05_07"],
    "M05_09": ["M05_07", "M05_08"],
    "M05_10": ["M05_02"],
    "M05_11": ["M05_10"],
    "M05_12": ["M05_11"],
    "M05_13": ["M05_01", "M05_02"],
    "M05_14": ["M05_03"],
    "M05_15": ["M05_02", "M05_03"]
  },
  recommendations: {
    "M05_01": ["M05_02", "M05_13"],
    "M05_02": ["M05_03", "M05_04", "M05_07"],
    "M05_03": ["M05_14"],
    "M05_04": ["M05_05"],
    "M05_05": ["M05_06"],
    "M05_06": ["M05_08"],
    "M05_07": ["M05_08", "M05_09"],
    "M05_08": ["M05_09"],
    "M05_09": [],
    "M05_10": ["M05_11"],
    "M05_11": ["M05_12"],
    "M05_12": [],
    "M05_13": ["M05_14"],
    "M05_14": ["M05_15"],
    "M05_15": []
  }
};

// 數據驗證函數
export const validateM05Data = () => {
  const issues = [];
  const expectedProblemsPerTopic = 6;

  // 檢查每個主題的問題數量
  M05_TOPICS.forEach(topic => {
    const problems = getProblemsByTopicId(topic.id);
    if (problems.length !== expectedProblemsPerTopic) {
      issues.push(`主題 ${topic.id} 問題數量不正確: ${problems.length}/${expectedProblemsPerTopic}`);
    }
  });

  // 檢查問題ID唯一性
  const allProblems = getAllM05Problems();
  const ids = allProblems.map(p => p.id);
  const uniqueIds = [...new Set(ids)];
  if (ids.length !== uniqueIds.length) {
    issues.push('存在重複的問題ID');
  }

  // 檢查必需字段
  allProblems.forEach(problem => {
    const requiredFields = ['id', 'topicId', 'question', 'solution', 'explanation', 'hint', 'difficulty', 'order', 'steps'];
    requiredFields.forEach(field => {
      if (!problem[field]) {
        issues.push(`問題 ${problem.id} 缺少字段: ${field}`);
      }
    });
  });

  // 檢查難度分佈合理性
  const difficultyStats = M05_STATISTICS.difficultyDistribution;
  if (difficultyStats.level1 === 0) {
    issues.push('缺少基礎難度問題');
  }
  if (difficultyStats.level4 > difficultyStats.level3) {
    issues.push('高難度問題比例過高');
  }

  return {
    isValid: issues.length === 0,
    issues,
    summary: {
      totalProblems: allProblems.length,
      expectedProblems: M05_TOPICS.length * expectedProblemsPerTopic,
      totalTopics: M05_TOPICS.length,
      validationTime: new Date().toISOString()
    }
  };
};

// 輔助函數：生成學習報告
export const generateLearningReport = (completedProblems = []) => {
  const totalProblems = getAllM05Problems().length;
  const completedCount = completedProblems.length;
  const completionRate = ((completedCount / totalProblems) * 100).toFixed(1);
  
  // 按主題統計完成情況
  const topicProgress = M05_TOPICS.map(topic => {
    const topicProblems = getProblemsByTopicId(topic.id);
    const topicCompleted = completedProblems.filter(id => 
      topicProblems.some(p => p.id === id)
    ).length;
    return {
      topicId: topic.id,
      title: topic.title,
      completed: topicCompleted,
      total: topicProblems.length,
      rate: ((topicCompleted / topicProblems.length) * 100).toFixed(1)
    };
  });
  
  // 按難度統計完成情況
  const difficultyProgress = [1, 2, 3, 4].map(level => {
    const levelProblems = getProblemsByDifficulty(level);
    const levelCompleted = completedProblems.filter(id =>
      levelProblems.some(p => p.id === id)
    ).length;
    return {
      level,
      completed: levelCompleted,
      total: levelProblems.length,
      rate: levelProblems.length > 0 ? ((levelCompleted / levelProblems.length) * 100).toFixed(1) : '0.0'
    };
  });
  
  return {
    overall: {
      completionRate,
      completed: completedCount,
      total: totalProblems,
      estimatedTimeSpent: Math.round(completedCount * (M05_STATISTICS.estimatedStudyTime / totalProblems))
    },
    byTopic: topicProgress,
    byDifficulty: difficultyProgress,
    recommendations: getNextRecommendations(completedProblems),
    achievements: getAchievements(completedProblems)
  };
};

// 輔助函數：獲取下一步建議
const getNextRecommendations = (completedProblems) => {
  const uncompletedTopics = M05_TOPICS.filter(topic => {
    const topicProblems = getProblemsByTopicId(topic.id);
    return !topicProblems.every(p => completedProblems.includes(p.id));
  });
  
  return uncompletedTopics.slice(0, 3).map(topic => ({
    topicId: topic.id,
    title: topic.title,
    reason: `建議學習${topic.title}，預計需要${topic.estimatedMinutes}分鐘`
  }));
};

// 輔助函數：獲取成就
const getAchievements = (completedProblems) => {
  const achievements = [];
  const completedCount = completedProblems.length;
  
  if (completedCount >= 10) achievements.push("百分數入門者");
  if (completedCount >= 30) achievements.push("百分數學習者");
  if (completedCount >= 60) achievements.push("百分數專家");
  if (completedCount >= 90) achievements.push("百分數大師");
  
  // 檢查特定主題完成情況
  const commercialProblems = getProblemsByApplication("商業");
  const completedCommercial = completedProblems.filter(id =>
    commercialProblems.some(p => p.id === id)
  ).length;
  if (completedCommercial >= commercialProblems.length * 0.8) {
    achievements.push("商業計算專家");
  }
  
  const financialProblems = getProblemsByApplication("金融");
  const completedFinancial = completedProblems.filter(id =>
    financialProblems.some(p => p.id === id)
  ).length;
  if (completedFinancial >= financialProblems.length * 0.8) {
    achievements.push("金融理財高手");
  }
  
  return achievements;
};

// 導出所有數據和函數
export default {
  M05_MODULE_INFO,
  M05_TOPICS,
  M05_PROBLEMS_DATA,
  getAllM05Problems,
  getProblemsByTopicId,
  getProblemsByDifficulty,
  getTopicById,
  searchProblems,
  getProblemsByApplication,
  getRecommendedLearningPath,
  M05_STATISTICS,
  M05_DEPENDENCIES,
  validateM05Data,
  generateLearningReport
}; 